import { useAppDispatch, useAppSelector } from 'hooks/redux';
import React from 'react';
import { v4 as uuid } from 'uuid';

import { categoriesActions, categoriesSelectors, categoriesTypes } from 'app-components/flux/categories';
import { actionHelpers } from 'app-components/flux/core';

import type { Category, CategoryProps, SubmitPayload } from 'app-components/flux/categories/categoriesTypes';
import { clearState } from 'app-components/flux/categories/categoryFormBuilder';
import { FormProvider } from 'app-components/flux/categories/categoryFormHooks';

import { getLogger, tracker } from 'app-components/utils/core';
import type { SubmitHandler } from 'app-components/utils/createForm/formBuilder';
import UpsertCategoryForm from './UpsertCategoryForm';

import { getDepthOfCategory } from './utils';

const log = getLogger('shares/AddCategoryDialog');

type Props = {
  category: Category;
  enableNewCategorySettings: boolean;
  enableTaxItems: boolean;
  enableUsageTypes: boolean;
  onClose: () => void;
};

const UpsertCategoryDialog = ({
  category,
  enableNewCategorySettings,
  enableTaxItems,
  enableUsageTypes,
  onClose,
}: Props) => {
  const dispatch = useAppDispatch();
  const categoriesById = useAppSelector(categoriesSelectors.getCategoriesById);

  const onSubmit: SubmitHandler<SubmitPayload, boolean> = async ({ payload, additionalArg: submittable }) => {
    if (!submittable) {
      return;
    }
    const data = payload.category;

    const upsertCategoryPromise = actionHelpers.bindPromiseAction(
      dispatch,
      !category ? categoriesActions.createCategory : categoriesActions.updateCategory,
    );

    const categoryToUpsert = categoriesTypes.mkCategory({
      ...(category && { id: category.id }),
      ...(!category ? { clientId: uuid().toUpperCase() } : { clientId: undefined }),
      ...(data.parentId && { parentId: data.parentId }),
      name: data.name,
      ...(enableUsageTypes && { usageType: data.usageType }),
      type: data.type,
      description: data.description ?? '',
      isExcludedFromBudgets: data.isExcludedFromBudgets,
      isExcludedFromReports: data.isExcludedFromReports,
      txfId: data.taxRelated ? data.txfId : '',
      txfIds: data.txfIds,
    });

    try {
      await upsertCategoryPromise(categoryToUpsert, {
        undo: { userMessage: `Category ${!category ? 'created.' : 'updated.'}` },
      });

      const depth =
        categoryToUpsert.parentId && categoryToUpsert.parentId !== '0'
          ? getDepthOfCategory(categoriesById.get(categoryToUpsert.parentId), categoriesById) + 1
          : 1;

      tracker.track(!category ? tracker.events.categoryCreate : tracker.events.categoryEdit, {
        usage_type: categoryToUpsert.usageType,
        type: categoryToUpsert.type,
        level: depth,
        name: categoryToUpsert.name,
        // has_description: categoryToUpsert.description !== '',
      });

      onClose();
    } catch (error) {
      log.error('Error creating category', error);
    }
  };

  return (
    <FormProvider input={(category?.toJS() as CategoryProps) || clearState} onSubmit={onSubmit}>
      <UpsertCategoryForm
        onClose={onClose}
        enableNewCategorySettings={enableNewCategorySettings}
        enableTaxItems={enableTaxItems}
        enableUsageTypes={enableUsageTypes}
      />
    </FormProvider>
  );
};

export default UpsertCategoryDialog;
